/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { getRuntimeConfig } from './runtime-config';

export const AUTH_PROVIDERS = ['auth0', 'simple_token', 'entra_id'] as const;
export type AuthProvider = (typeof AUTH_PROVIDERS)[number];

type AuthProviderConfig = Readonly<{
  browserManagedSession: boolean;
  serverManagedToken: boolean;
  usesAuth0Session: boolean;
  usesProxy: boolean;
}>;

const AUTH_PROVIDER_CONFIG: Record<AuthProvider, AuthProviderConfig> = {
  auth0: {
    browserManagedSession: false,
    serverManagedToken: false,
    usesAuth0Session: true,
    usesProxy: false,
  },
  simple_token: {
    browserManagedSession: false,
    serverManagedToken: true,
    usesAuth0Session: false,
    usesProxy: true,
  },
  entra_id: {
    browserManagedSession: true,
    serverManagedToken: false,
    usesAuth0Session: false,
    usesProxy: false,
  },
};

/**
 * Strict: null for unset or unrecognised input, so callers can tell
 * "not configured" from "configured as auth0".
 */
export const parseAuthProvider = (value?: unknown): AuthProvider | null => {
  if (typeof value !== 'string') return null;

  const provider = value.trim().toLowerCase();

  return provider && AUTH_PROVIDERS.includes(provider as AuthProvider)
    ? (provider as AuthProvider)
    : null;
};

/**
 * Throws instead of defaulting: an unset or misspelled AUTH_PROVIDER must fail
 * loudly rather than silently serving the app under some assumed provider.
 */
const normalizeAuthProvider = (value?: unknown): AuthProvider => {
  const provider = parseAuthProvider(value);
  if (provider) return provider;

  throw new Error(
    `AUTH_PROVIDER is not set. Please set AUTH_PROVIDER to one of: ${AUTH_PROVIDERS.join(', ')}, and provide the required credentials for that auth option.`
  );
};

// Env vars each provider needs beyond AUTH_PROVIDER itself. Checked server-side
// so a misconfigured deployment fails loudly instead of erroring per API call.
const REQUIRED_ENV_VARS: Partial<Record<AuthProvider, readonly string[]>> = {
  entra_id: ['AZURE_CLIENT_ID', 'AZURE_TENANT_ID', 'AZURE_REDIRECT_URI'],
  simple_token: ['OKAHU_AUTH_TOKEN', 'OKAHU_API_KEY'],
  auth0: [
    'AUTH0_SECRET',
    'AUTH0_BASE_URL',
    'AUTH0_ISSUER_BASE_URL',
    'AUTH0_CLIENT_ID',
    'AUTH0_CLIENT_SECRET',
    'AUTH0_SCOPE',
  ],
};

const assertRequiredCredentials = (provider: AuthProvider): void => {
  const missing = (REQUIRED_ENV_VARS[provider] ?? []).filter(
    (key) => !process.env[key]
  );

  if (missing.length > 0) {
    throw new Error(
      `Missing required credentials for AUTH_PROVIDER=${provider}: ${missing.join(', ')}. Please set ${missing.length > 1 ? 'them' : 'it'} in your environment.`
    );
  }
};

export const getAuthProvider = (): AuthProvider => {
  const provider = normalizeAuthProvider(process.env.AUTH_PROVIDER);
  assertRequiredCredentials(provider);

  return provider;
};

export const getClientAuthProvider = (): AuthProvider =>
  normalizeAuthProvider(getRuntimeConfig().authProvider);

export const getAuthProviderConfig = (
  provider: AuthProvider = getAuthProvider()
): AuthProviderConfig => AUTH_PROVIDER_CONFIG[provider];

export const getClientAuthProviderConfig = (): AuthProviderConfig =>
  getAuthProviderConfig(getClientAuthProvider());

export const isSimpleTokenAuth = () => getAuthProvider() === 'simple_token';

export const getSimpleTokenHeaders = () => {
  const token = process.env.OKAHU_AUTH_TOKEN;
  const apiKey = process.env.OKAHU_API_KEY;

  if (!token || !apiKey) {
    throw new Error(
      'OKAHU_AUTH_TOKEN and OKAHU_API_KEY are required when AUTH_PROVIDER=simple_token'
    );
  }

  return {
    Authorization: `Bearer ${token}`,
    'x-api-key': apiKey,
  };
};
